/* eslint-disable no-nested-ternary */

import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

// @mui
import { useTheme } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';

// routes
import { RouterLink } from 'src/routes/components';

// hooks
import { usePlanFeature } from 'src/hooks/usePlanFeature';

// redux
import { openUpgradeDialog } from 'src/redux/slices/planSlice';

// components
import PlanBadge from 'src/components/plan-badge/PlanBadge';
import Iconify from '../../iconify';

// styles
import { StyledItem, StyledIcon } from './styles';

// ----------------------------------------------------------------------

const NavItem = forwardRef(({ item, depth, open, active, externalLink, config, ...other }, ref) => {
  const drawerWidth = useSelector((state) => state.drawer.drawerWidth);

  const theme = useTheme();

  const { title, path, icon, info, children, disabled, caption, feature } = item;

  const dispatch = useDispatch();
  const { isLocked, requiredPlan, shouldShowBadge, type, value, badgePlan } = usePlanFeature(feature);

  const textRef = useRef(null);
  const titleContainerRef = useRef(null);
  const measureRef = useRef(null);

  // ----------------------------------------------------------------------
  // State
  // ----------------------------------------------------------------------

  const [isTruncated, setIsTruncated] = useState(false);

  // ----------------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------------

  const subItem = depth !== 1;

  // Mini root item = icon-only title
  const isMiniRoot = config?.hiddenLabel && !subItem;

  // Only true when children actually exist
  const hasChildren = Array.isArray(children) ? children.length > 0 : Boolean(children);

  // Show tooltip on mini root ONLY when there is no popup.
  //
  // When the item has children, its title is rendered
  // inside the popup instead.
  const showMiniRootTooltip = isMiniRoot && !hasChildren;

  // Normal submenu tooltip only when text is really truncated
  const showTextTooltip = !isMiniRoot && isTruncated;

  // ----------------------------------------------------------------------
  // Text measurement
  // ----------------------------------------------------------------------

  const checkTruncation = useCallback(() => {
    if (isMiniRoot || !titleContainerRef.current || !measureRef.current) {
      setIsTruncated(false);
      return;
    }

    const containerWidth = titleContainerRef.current.clientWidth;

    const fullTextWidth = measureRef.current.scrollWidth;

    setIsTruncated(fullTextWidth > containerWidth);
  }, [isMiniRoot]);

  useEffect(() => {
    if (isMiniRoot) {
      setIsTruncated(false);
      return undefined;
    }

    let frameId;

    const runCheck = () => {
      frameId = requestAnimationFrame(() => {
        checkTruncation();
      });
    };

    runCheck();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
          runCheck();
        })
        : null;

    if (resizeObserver) {
      if (textRef.current) {
        resizeObserver.observe(textRef.current);
      }

      if (titleContainerRef.current) {
        resizeObserver.observe(titleContainerRef.current);
      }
    }

    window.addEventListener('resize', runCheck);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      resizeObserver?.disconnect();

      window.removeEventListener('resize', runCheck);
    };
  }, [drawerWidth, title, isMiniRoot, checkTruncation]);

  // ----------------------------------------------------------------------
  // Click
  // ----------------------------------------------------------------------

  const handleClick = (e) => {
    if (isLocked) {
      e.preventDefault();
      e.stopPropagation();

      dispatch(
        openUpgradeDialog({
          requiredPlan,
          featureLabel: title,
          featureType: type,
          limitValue: value,
        })
      );
    } else if (other.onClick) {
      other.onClick(e);
    }
  };

  // ----------------------------------------------------------------------
  // Title
  // ----------------------------------------------------------------------

  const renderTitle = (
    <Box
      ref={titleContainerRef}
      component="span"
      sx={{
        display: 'block',
        width: 1,
        minWidth: 0,
        maxWidth: '100%',

        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </Box>
  );

  const renderTitleWithTooltip = showTextTooltip ? (
    <Tooltip title={title} arrow placement="top" disableInteractive>
      {renderTitle}
    </Tooltip>
  ) : (
    renderTitle
  );

  // ----------------------------------------------------------------------
  // Content
  // ----------------------------------------------------------------------

  const renderContent = (
    <>
      {/* Hidden element used only for measuring title width */}
      {!isMiniRoot && (
        <Box
          ref={measureRef}
          sx={{
            position: 'absolute',
            top: -9999,
            left: -9999,

            visibility: 'hidden',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',

            fontSize: subItem ? theme.typography.body2.fontSize : 10,

            lineHeight: subItem ? theme.typography.body2.lineHeight : '16px',

            fontWeight: active
              ? theme.typography.fontWeightBold
              : theme.typography.fontWeightMedium,
          }}
        >
          {title}
        </Box>
      )}

      <StyledItem
        disableGutters
        ref={ref}
        open={open}
        depth={depth}
        active={active}
        disabled={disabled}
        config={config}
        className={active ? 'active' : ''}
        {...other}
        onClick={handleClick}
      >
        {/* ============================================================
              MAIN ICON
              ============================================================ */}

        {icon && (
          <Tooltip
            title={showMiniRootTooltip || showTextTooltip ? title : ''}
            arrow
            placement="right"
            disableHoverListener={!showMiniRootTooltip && !showTextTooltip}
            disableInteractive
          >
            <StyledIcon
              size={config?.iconSize || 28}
              sx={{
                width: config?.iconSize || 28,

                height: config?.iconSize || 28,

                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',

                flexShrink: 0,

                ...(subItem && {
                  mr: 1.5,
                }),

                transition: 'transform 0.2s ease-in-out',

                '&:hover': {
                  transform: 'scale(1.08)',
                },
              }}
            >
              {icon}
            </StyledIcon>
          </Tooltip>
        )}

        {/* ============================================================
              TITLE
              ============================================================ */}

        {!isMiniRoot && (
          <ListItemText
            ref={textRef}
            sx={{
              flex: 1,
              minWidth: 0,
              width: 1,

              // Reserve space for the child arrow.
              ...(hasChildren && {
                pr: 3.5,
              }),

              ...(!subItem && {
                px: 0.5,
                mt: 0.5,
              }),

              fontSize: subItem ? theme.typography.body2.fontSize : 10,

              lineHeight: subItem ? theme.typography.body2.lineHeight : '16px',

              textAlign: subItem ? 'left' : 'center',

              fontWeight: active
                ? theme.typography.fontWeightBold
                : theme.typography.fontWeightSemiBold,

              transition: 'font-weight 0.3s ease',

              '& .MuiListItemText-primary': {
                width: 1,
                minWidth: 0,
                overflow: 'hidden',
                display: 'block',
              },
            }}
            primary={
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',

                  justifyContent: subItem ? 'flex-start' : 'center',

                  width: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                {renderTitleWithTooltip}
              </Box>
            }
            primaryTypographyProps={{
              noWrap: true,
              textTransform: 'capitalize',

              sx: {
                width: 1,
                minWidth: 0,
                overflow: 'hidden',
              },
            }}
          />
        )}

        {/* ============================================================
              PLAN BADGE
              ============================================================ */}

        {isMiniRoot && shouldShowBadge && (
          <Box
            sx={{
              position: 'absolute',
              top: 7,
              left: 8,
              zIndex: 2,
            }}
          >
            <PlanBadge plan={isLocked ? requiredPlan : badgePlan} isLocked={isLocked} variant="mini" />
          </Box>
        )}

        {/* ============================================================
              CAPTION
              ============================================================ */}

        {caption && (
          <Tooltip title={caption} arrow placement="right">
            <Iconify
              width={16}
              icon="eva:info-outline"
              sx={{
                color: 'text.disabled',

                transition: 'color 0.3s ease',

                '&:hover': {
                  color: 'text.primary',
                },

                ...(!subItem && {
                  top: 11,
                  left: 6,
                  position: 'absolute',
                }),
              }}
            />
          </Tooltip>
        )}

        {/* ============================================================
              INFO
              ============================================================ */}

        {item.title !== 'Approvals' && info && (
          <>
            <Box
              component="span"
              sx={{
                ml: 1,
                lineHeight: 0,

                ...(isMiniRoot && {
                  position: 'absolute',
                  top: '50%',
                  right: 25,
                  transform: 'translateY(-50%)',
                  margin: 0,
                }),
              }}
            >
              {info}
            </Box>

            {!isMiniRoot && (
              <Box
                width={16}
                sx={{
                  ml: 1,
                  flexShrink: 0,
                }}
              />
            )}
          </>
        )}

        {/* ============================================================
              APPROVALS INFO
              ============================================================ */}

        {item.title === 'Approvals' && info && (
          <Box
            component="span"
            sx={{
              top: 10,
              left: 6,
              position: 'absolute',
            }}
          >
            {info}
          </Box>
        )}

        {/* ============================================================
              CHILD ARROW
              ============================================================ */}

        {hasChildren && (
          <Iconify
            width={16}
            icon="eva:arrow-ios-forward-fill"
            sx={{
              position: 'absolute',

              top: '50%',
              right: 6,

              transform: 'translateY(-50%)',

              flexShrink: 0,

              transition: 'transform 0.2s ease',

              '&:hover': {
                transform: 'translate(3px, -50%)',
              },
            }}
          />
        )}
      </StyledItem>
    </>
  );

  // ----------------------------------------------------------------------
  // External link
  // ----------------------------------------------------------------------

  if (externalLink) {
    return (
      <Link
        href={path}
        target="_blank"
        rel="noopener"
        underline="none"
        onClick={handleClick}
        sx={{
          width: 1,

          ...(disabled && {
            cursor: 'default',
          }),
        }}
      >
        {renderContent}
      </Link>
    );
  }

  // ----------------------------------------------------------------------
  // Internal link
  // ----------------------------------------------------------------------

  return (
    <Link
      component={RouterLink}
      href={path}
      underline="none"
      onClick={handleClick}
      sx={{
        width: 1,

        ...(disabled && {
          cursor: 'default',
        }),
      }}
    >
      {renderContent}
    </Link>
  );
});

NavItem.displayName = 'NavItem';

NavItem.propTypes = {
  active: PropTypes.bool,
  config: PropTypes.object,
  depth: PropTypes.number,
  externalLink: PropTypes.bool,
  item: PropTypes.object,
  open: PropTypes.bool,
};

export default NavItem;
